import { useEffect, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import api, { API } from "../lib/api";
import { OuterNav } from "../layouts/PublicNav";
import { Headphones, Sparkle, Lock } from "@phosphor-icons/react";
import ArticleBlocks from "../components/ArticleBlocks";
import SeoPageHead from "../components/SeoPageHead";
import { ShareButton } from "../components/ShareButton";
import { useLanguage } from "../hooks/useLanguage";
import { useSectorLabels } from "../lib/useSectorLabels";
import { formatDate } from "../lib/date";

// PHASE 8.45 — Tier badge for public + panelist Editorial views.
const TIER_META = {
  public:      { label: "Public",      cls: "bg-emerald-600" },
  panelists:   { label: "Panelists",   cls: "bg-sky-600" },
  subscribers: { label: "Subscribers", cls: "bg-violet-600" },
  enterprise:  { label: "Enterprise",  cls: "bg-black" },
};
function TierBadge({ tier }) {
  const m = TIER_META[tier] || TIER_META.public;
  return <span className={`${m.cls} text-white px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider`}>{m.label}</span>;
}

function usePathPrefix(section) {
  const loc = useLocation();
  if (loc.pathname.startsWith(`/panelist/${section}`)) return `/panelist/${section}`;
  return `/${section}`;
}

// Cache-busting version for share links. WhatsApp/Facebook/Twitter cache a
// link's preview (title/image) keyed by the EXACT url string, sometimes for
// days/weeks, and never re-check it even after the article's cover_image is
// edited later. Appending a short hash of the fields that actually affect
// the preview card means editing the cover image (or title) automatically
// changes the share URL, forcing every platform to re-crawl fresh instead
// of showing a stale "no image" (or old image) card from before the edit.
function _ogVersion(a) {
  const s = `${a?.cover_image || ""}|${a?.title || ""}`;
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h).toString(36);
}

export function ArticlesList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const sector = params.get("sector");
  const audio_only = params.get("has_audio");
  const prefix = usePathPrefix("articles");
  // ITER 68 — reactive Urdu: refetch (title/teaser projection) live when the
  // language toggle flips, matching the pattern used on ArticleDetail.
  const { lang } = useLanguage();
  const { sectorLabel } = useSectorLabels();
  useEffect(() => {
    const q = new URLSearchParams();
    if (sector) q.set("sector", sector);
    if (audio_only) q.set("has_audio", "true");
    q.set("lang", lang);
    setLoading(true);
    api.get(`/articles?${q.toString()}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [sector, audio_only, lang]);

  return (
    <div className="min-h-screen themable-public">
      <SeoPageHead
        pageKey="articles"
        path="/articles"
        defaultTitle="Reports & Articles — Pakistan Consumer Insights | oodel."
        defaultDescription="Original research, audio deep-dives and weekly briefings on Pakistani consumers."
      />
      <OuterNav />
      <div className="max-w-[1100px] mx-auto px-6 py-12">
        <div className="pb-4 mb-10">
          <div className="overline text-mango">EDITORIAL</div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-neutral-900">{audio_only ? "Deep Dives" : "Reports & Articles"}</h1>
          <p className="text-neutral-500 mt-3 max-w-2xl">{audio_only ? "Audio briefings narrated from the latest oodel. waves." : "Long-form analysis of every wave we publish."}</p>
        </div>
        <div className="space-y-4" data-testid="articles-list">
          {items.map((a) => (
            <Link key={a.id} to={`${prefix}/${a.slug || a.id}`} className="bg-white border border-neutral-200 rounded-2xl p-6 md:p-8 block group relative shadow-sm hover:shadow-md hover:border-mango/40 transition-all" data-testid={`article-${a.id}`}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
                {/* ITER 94 — blog-style banner thumbnail, same cover_image
                    used for WhatsApp/OG sharing. Always reserve the slot
                    (fallback solid block if an article has no cover) so
                    the list stays visually aligned. */}
                <div className="md:col-span-3 aspect-video overflow-hidden rounded-xl bg-neutral-900" data-testid={`article-cover-${a.id}`}>
                  {a.cover_image ? (
                    <img src={a.cover_image} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-neutral-800 to-black" />
                  )}
                </div>
                <div className="md:col-span-2 overline text-neutral-400">
                  <div className="text-mango">{sectorLabel(a.sector, lang).toUpperCase()}</div>
                  <div className="mt-1">{formatDate(a.created_at)}</div>
                  <div className="mt-2 flex flex-col gap-1.5 items-start">
                    <TierBadge tier={a.access_tier || "public"} />
                    {a.tier_label && <span className="text-[10px] text-neutral-400 uppercase tracking-wider">{a.tier_label}</span>}
                    {a.audio_url && <span className="inline-flex items-center gap-1 text-neutral-700"><Headphones size={12} /> AUDIO</span>}
                  </div>
                </div>
                <div className="md:col-span-7">
                  <h2 className="text-xl md:text-2xl font-bold tracking-tight text-neutral-900 group-hover:text-mango transition-colors flex items-start gap-2">
                    {a.locked && <Lock size={20} className="mt-1 flex-shrink-0 text-neutral-300" />}
                    <span>{a.title}</span>
                  </h2>
                  {a.locked && (
                    <p className="mt-3 text-sm text-neutral-500 line-clamp-3">
                      {a.teaser || (a.lock_reason === "profile_incomplete" ? "Complete your profile to read this Panelist-only report." : "This report is available to higher-tier subscribers only.")}
                    </p>
                  )}
                  {a.locked && (
                    <div className="mt-3 text-xs text-mango font-bold uppercase tracking-wider">{a.lock_reason === "profile_incomplete" ? "Complete your profile to unlock →" : "Upgrade to unlock →"}</div>
                  )}
                </div>
              </div>
            </Link>
          ))}
          {!loading && items.length === 0 && <div className="bg-white border border-neutral-200 rounded-2xl p-8 text-neutral-500">No articles yet.</div>}
        </div>
      </div>
    </div>
  );
}

export function ArticleDetail() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  // ITER 67 — Language is a REACTIVE hook, not a one-time localStorage read.
  // This is what makes toggling EN↔اردو re-fetch the article live (previously
  // required a page refresh).
  const { lang } = useLanguage();
  const { sectorLabel } = useSectorLabels();
  const prefix = usePathPrefix("articles");
  useEffect(() => {
    api.get(`/articles/${id}?lang=${lang}`).then((r) => setA(r.data));
  }, [id, lang]);
  if (!a) return <div className="p-12">Loading…</div>;
  const isLocked = !!a.locked;

  return (
    <div className="min-h-screen themable-public">
      <SeoPageHead
        pageKey="articles"
        path={`/articles/${a.slug || a.id}`}
        defaultTitle={`${a.title} | oodel.`}
        defaultDescription={(a.standfirst || a.excerpt || a.teaser || "").slice(0, 200)}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": a.title,
          "datePublished": a.created_at,
          "image": a.cover_image || undefined,
          "author": { "@type": "Organization", "name": "oodel." },
          "publisher": { "@type": "Organization", "name": "oodel." },
        }}
      />
      <OuterNav />
      <article className="max-w-[820px] mx-auto px-6 py-12">
        <div className="overline mb-4 flex items-center gap-3 flex-wrap">
          <Link to={`/sectors/${a.sector}`} className="text-mango hover:underline">{sectorLabel(a.sector, lang).toUpperCase()}</Link>
          <span>·</span>
          <span>{formatDate(a.created_at)}</span>
          <TierBadge tier={a.access_tier || "public"} />
          {a.tier_label && <span className="text-neutral-500">· {a.tier_label}</span>}
          <span className="ml-auto">
            <ShareButton
              url={`${API}/share/articles/${a.slug || a.id}?v=${_ogVersion(a)}`}
              title={a.title}
              text={a.title}
              lang={lang}
              testidPrefix="article-share"
            />
          </span>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight leading-[1.15] text-neutral-900" data-testid="article-title">{a.title}</h1>
        {a.cover_image && <img src={a.cover_image} alt="" className="w-full aspect-video object-cover rounded-2xl mt-8" data-testid="article-cover-image" />}

        {a.audio_url && a.audio_enabled !== false && !isLocked && (
          <div className="mt-8 rounded-2xl bg-neutral-900 text-white p-5">
            <div className="flex items-center gap-2 overline text-mango mb-3"><Headphones size={14} /> DEEP DIVE — LISTEN</div>
            <audio data-testid="article-audio" src={`${process.env.REACT_APP_BACKEND_URL}${a.audio_url}`} controls className="w-full" />
          </div>
        )}

        <div className="mt-10">
          {isLocked ? (
            <div>
              {/* Teaser blocks + paywall */}
              {a.teaser_blocks && a.teaser_blocks.length > 0
                ? <ArticleBlocks blocks={a.teaser_blocks} lang={lang} />
                : <p className="text-lg text-neutral-700 leading-relaxed" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }} dir={lang === "ur" ? "rtl" : undefined}>{a.teaser}</p>
              }
              <div className="mt-10 rounded-2xl p-8 bg-neutral-50 border border-neutral-200" data-testid="article-paywall">
                <div className="flex items-center gap-3 mb-3">
                  <Lock size={20} className="text-mango" />
                  <div className="overline">{a.lock_reason === "profile_incomplete" ? "COMPLETE YOUR PROFILE" : <>RESTRICTED — <TierBadge tier={a.access_tier} /> tier</>}</div>
                </div>
                {a.lock_reason === "profile_incomplete" ? (
                  <>
                    <h2 className="text-2xl font-bold tracking-tight text-neutral-900">Complete your profile to read this</h2>
                    <p className="text-neutral-500 mt-2">
                      Panelist-only reports need a completed profile so we know your demographic slice is real — takes about 2 minutes.
                    </p>
                    <div className="mt-5 flex gap-2 flex-wrap">
                      <Link to="/panelist/profile" className="bg-mango text-white rounded-full px-5 py-2.5 font-bold hover:bg-mango/90 transition-colors" data-testid="article-complete-profile-cta">
                        Complete profile →
                      </Link>
                    </div>
                  </>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold tracking-tight text-neutral-900">Unlock the full report</h2>
                    <p className="text-neutral-500 mt-2">
                      This deep-dive is available to <strong>{TIER_META[a.access_tier]?.label}</strong> readers only. Upgrade your account
                      to read the full analysis and access every companion tier.
                    </p>
                    <div className="mt-5 flex gap-2 flex-wrap">
                      <Link to={`/register?next=${encodeURIComponent(`/panelist/articles/${a.slug || a.id}`)}`} className="bg-mango text-white rounded-full px-5 py-2.5 font-bold hover:bg-mango/90 transition-colors" data-testid="article-upsell-cta">
                        Upgrade & unlock →
                      </Link>
                      <Link
                        to={`/login?next=${encodeURIComponent(`/panelist/articles/${a.slug || a.id}`)}`}
                        className="border border-neutral-300 rounded-full px-5 py-2.5 font-bold hover:border-mango/50 transition-colors"
                        data-testid="article-paywall-signin"
                      >
                        Sign in
                      </Link>
                    </div>
                  </>
                )}
              </div>
              {a.companions && a.companions.length > 0 && (
                <div className="mt-10">
                  <div className="overline mb-3 text-mango">COMPANION EDITORIALS</div>
                  <div className="space-y-2">
                    {a.companions.map((c) => (
                      <Link
                        key={c.id}
                        to={`${prefix}/${c.slug || c.id}`}
                        className={`block rounded-xl border ${c.locked ? "border-neutral-200 bg-neutral-50" : "border-neutral-200 hover:border-mango/40 hover:bg-mango/5"} p-4 transition-colors`}
                        data-testid={`article-companion-${c.id}`}
                      >
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <TierBadge tier={c.access_tier} />
                          {c.locked && <Lock size={12} className="text-neutral-400" />}
                          {c.tier_label && <span className="text-[10px] uppercase tracking-wider text-neutral-400">{c.tier_label}</span>}
                        </div>
                        <div className="font-bold text-neutral-900">{c.title}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <ArticleBlocks blocks={a.blocks} body={a.body} lang={lang} />
              {a.companions && a.companions.length > 0 && (
                <div className="mt-12 pt-6 border-t border-neutral-200">
                  <div className="overline mb-3 text-mango">COMPANION EDITORIALS</div>
                  <div className="space-y-2">
                    {a.companions.map((c) => (
                      <Link
                        key={c.id}
                        to={`${prefix}/${c.slug || c.id}`}
                        className={`block rounded-xl border ${c.locked ? "border-neutral-200 bg-neutral-50" : "border-neutral-200 hover:border-mango/40 hover:bg-mango/5"} p-4 transition-colors`}
                        data-testid={`article-companion-${c.id}`}
                      >
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <TierBadge tier={c.access_tier} />
                          {c.locked && <Lock size={12} className="text-neutral-400" />}
                          {c.tier_label && <span className="text-[10px] uppercase tracking-wider text-neutral-400">{c.tier_label}</span>}
                        </div>
                        <div className="font-bold text-neutral-900">{c.title}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="mt-16 pt-6 border-t border-neutral-200 text-sm text-neutral-500 flex justify-between">
          <Link to={prefix} className="hover:text-mango">← All articles</Link>
          <span style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>oodel<span className="text-mango">.</span> editorial</span>
        </div>
      </article>
    </div>
  );
}
